import React, { useState, useEffect } from 'react';
import axios from '@/lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { 
  X, 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  CheckCircle, 
  RefreshCw,
  Bot,
  ChevronDown,
  ChevronUp,
  Check,
  XCircle,
  Sparkles,
  FileEdit,
  Eye,
  Clock,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    'CLEAR': { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle },
    'REVIEW_REQUIRED': { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertCircle },
    'HIGH_RISK': { color: 'bg-red-100 text-red-800 border-red-300', icon: AlertTriangle }
  };
  
  const { color, icon: Icon } = config[status] || config['REVIEW_REQUIRED'];
  
  return (
    <Badge className={`${color} border px-3 py-1 flex items-center gap-1`}>
      <Icon className="w-4 h-4" />
      {status?.replace('_', ' ')}
    </Badge>
  );
};

// Severity badge component
const SeverityBadge = ({ severity }) => {
  const config = {
    'HIGH': { color: 'bg-red-100 text-red-700', icon: '🔴' },
    'MEDIUM': { color: 'bg-yellow-100 text-yellow-700', icon: '🟡' },
    'LOW': { color: 'bg-blue-100 text-blue-700', icon: '🟢' }
  };
  
  const { color, icon } = config[severity] || config['MEDIUM'];
  
  return (
    <span className={`${color} px-2 py-0.5 rounded text-xs font-medium`}>
      {icon} {severity}
    </span>
  );
};

// Rule Check Row Component for AP/TS rule summary
const RuleCheckRow = ({ label, status, detail }) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'CLEAR':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'HIGH_RISK':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'NEEDS_REVIEW':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'NOT_APPLICABLE':
        return <span className="w-4 h-4 text-gray-400">—</span>;
      default:
        return <Info className="w-4 h-4 text-gray-400" />;
    }
  };
  
  const getStatusColor = () => {
    switch (status) {
      case 'CLEAR':
        return 'text-green-700 bg-green-50';
      case 'HIGH_RISK':
        return 'text-red-700 bg-red-50';
      case 'NEEDS_REVIEW':
        return 'text-yellow-700 bg-yellow-50';
      default:
        return 'text-gray-500 bg-gray-50';
    }
  };
  
  return (
    <div className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
      <div className="flex items-center gap-2">
        {getStatusIcon()}
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {detail && (
          <span className="text-xs text-gray-500">{detail}</span>
        )}
        <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor()}`}>
          {status === 'NOT_APPLICABLE' ? 'N/A' : status?.replace('_', ' ') || 'Pending'}
        </span>
      </div>
    </div>
  );
};

// Issue card component (enhanced with rule code) - REQUIRES ADVOCATE REVIEW
const IssueCard = ({ issue, index, onAcknowledge, reviewId }) => {
  const [expanded, setExpanded] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [note, setNote] = useState('');
  const [showNoteField, setShowNoteField] = useState(false);
  
  const handleAcknowledge = async (withNote = false) => {
    try {
      await onAcknowledge(reviewId, 'acknowledge_issue', { 
        issue_index: index,
        note: withNote ? note : null,
        acknowledged_at: new Date().toISOString()
      });
      setAcknowledged(true);
      setShowNoteField(false);
      toast.success('Issue acknowledged and recorded');
    } catch (error) {
      toast.error('Failed to record acknowledgment');
    }
  };
  
  return (
    <div className={`border rounded-lg p-3 ${acknowledged ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <SeverityBadge severity={issue.severity} />
            {issue.rule_code && (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                {issue.rule_code}
              </Badge>
            )}
            <span className="text-sm font-medium text-gray-700">{issue.section}</span>
            {acknowledged && (
              <Badge className="text-xs bg-green-100 text-green-700 border-green-300">
                ✓ Reviewed
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-800">{issue.issue}</p>
        </div>
        <button 
          onClick={() => setExpanded(!expanded)}
          className="text-gray-400 hover:text-gray-600"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      
      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-2">
          <div>
            <span className="text-xs font-semibold text-gray-500">REASON:</span>
            <p className="text-sm text-gray-700">{issue.reason}</p>
          </div>
          {issue.suggestion && (
            <div>
              <span className="text-xs font-semibold text-gray-500">RECOMMENDED ACTION:</span>
              <p className="text-sm text-blue-700 italic">{issue.suggestion}</p>
            </div>
          )}
          
          {!acknowledged && (
            <div className="mt-3 pt-2 border-t border-amber-200">
              <p className="text-xs text-amber-700 mb-2">
                ⚠️ This issue requires your professional judgment. Acknowledge after verification.
              </p>
              
              {showNoteField ? (
                <div className="space-y-2">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note about how you addressed this issue (optional)..."
                    className="w-full text-sm border rounded p-2 h-20"
                  />
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleAcknowledge(true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-3 h-3 mr-1" /> Acknowledge with Note
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowNoteField(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => handleAcknowledge(false)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-3 h-3 mr-1" /> Acknowledge
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowNoteField(true)}
                  >
                    Add Note
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {acknowledged && (
            <div className="mt-2 p-2 bg-green-100 rounded text-sm text-green-700 flex items-center gap-1">
              <Check className="w-4 h-4" /> Reviewed and acknowledged by advocate
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Enhanced Suggested Edit Card with Option D (Hybrid) workflow
const SuggestedEditCard = ({ 
  edit, 
  index, 
  reviewId, 
  opinionId,
  onAction, 
  onApplySuggestion,
  isOpinionSigned 
}) => {
  const [status, setStatus] = useState(null); // null | 'accepted_pending' | 'applied' | 'rejected'
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [applying, setApplying] = useState(false);
  
  const handleAccept = async () => {
    try {
      await onAction(reviewId, 'accept_suggestion', { edit_index: index, status: 'pending' });
      setStatus('accepted_pending');
      setShowPreview(true);
      toast.success('Suggestion accepted - Review preview before applying');
    } catch (error) {
      toast.error('Failed to record action');
    }
  };
  
  const handleReject = async () => {
    try {
      await onAction(reviewId, 'reject_suggestion', { edit_index: index });
      setStatus('rejected');
      setShowPreview(false);
      toast.info('Suggestion rejected');
    } catch (error) {
      toast.error('Failed to record action');
    }
  };
  
  const handleRevertPreview = () => {
    setShowPreview(false);
    setStatus(null);
  };
  
  const handleApplyChange = async () => {
    setApplying(true);
    try {
      await onApplySuggestion(opinionId, reviewId, index, edit);
      setStatus('applied');
      setShowPreview(false);
      setShowConfirmModal(false);
      toast.success('Change applied - New version created');
    } catch (error) {
      toast.error('Failed to apply change: ' + (error.response?.data?.detail || error.message));
    } finally {
      setApplying(false);
    }
  };
  
  return (
    <>
      <div className={`border rounded-lg p-3 ${status === 'applied' ? 'bg-green-50 border-green-300' : status === 'rejected' ? 'bg-gray-50' : 'bg-blue-50 border-blue-200'}`}>
        <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-2">
          <FileEdit className="w-3 h-3 text-blue-500" />
          <span className="text-blue-600">Language Improvement</span>
          <span className="text-gray-400">•</span>
          {edit.location}
        </div>
        
        {/* Diff Preview */}
        <div className="space-y-2">
          <div className={`p-2 rounded text-sm ${showPreview ? 'bg-red-100 border border-red-300' : 'bg-red-50'}`}>
            <span className="text-xs font-semibold text-red-600 flex items-center gap-1">
              {showPreview && <span className="text-red-500">−</span>} ORIGINAL:
            </span>
            <p className={`text-gray-700 ${showPreview ? 'line-through text-red-700' : ''}`}>{edit.original_text}</p>
          </div>
          
          <div className={`p-2 rounded text-sm ${showPreview ? 'bg-green-100 border border-green-300' : 'bg-green-50'}`}>
            <span className="text-xs font-semibold text-green-600 flex items-center gap-1">
              {showPreview && <span className="text-green-500">+</span>} SUGGESTED:
            </span>
            <p className={`text-gray-700 ${showPreview ? 'text-green-700 font-medium' : ''}`}>{edit.suggested_text}</p>
          </div>
          
          <div className="text-xs text-gray-500">
            <span className="font-semibold">Why:</span> {edit.reason}
          </div>
        </div>
        
        {/* Status: Not yet acted */}
        {!status && !isOpinionSigned && (
          <div className="mt-3">
            <p className="text-xs text-blue-600 mb-2">
              ✏️ This is a tone/clarity improvement. Does not change legal conclusions.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleAccept} className="flex-1 border-green-300 text-green-700 hover:bg-green-50">
                <Check className="w-3 h-3 mr-1" /> Accept
              </Button>
              <Button size="sm" variant="outline" onClick={handleReject} className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-50">
                <XCircle className="w-3 h-3 mr-1" /> Reject
              </Button>
            </div>
          </div>
        )}
        
        {/* Status: Signed opinion - cannot modify */}
        {isOpinionSigned && !status && (
          <div className="mt-3 text-xs text-amber-600 flex items-center gap-1 bg-amber-50 p-2 rounded">
            <Shield className="w-3 h-3" /> Opinion is signed - cannot apply changes
          </div>
        )}
        
        {/* Status: Accepted (Pending Apply) */}
        {status === 'accepted_pending' && showPreview && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Accepted (Pending Apply)</span>
            </div>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                onClick={() => setShowConfirmModal(true)} 
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Check className="w-3 h-3 mr-1" /> Apply Change
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleRevertPreview}
                className="flex-1"
              >
                <XCircle className="w-3 h-3 mr-1" /> Revert
              </Button>
            </div>
          </div>
        )}
        
        {/* Status: Applied */}
        {status === 'applied' && (
          <div className="mt-3 text-sm text-green-600 flex items-center gap-1 bg-green-50 p-2 rounded">
            <CheckCircle className="w-4 h-4" /> Applied - New version created
          </div>
        )}
        
        {/* Status: Rejected */}
        {status === 'rejected' && (
          <div className="mt-3 text-sm text-gray-500 flex items-center gap-1">
            <XCircle className="w-4 h-4" /> Rejected
          </div>
        )}
      </div>
      
      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Apply AI-Suggested Edit
            </DialogTitle>
            <DialogDescription>
              You are about to apply an AI-suggested edit to the opinion document.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-2">
              <div className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5" />
                <span>This will create a <strong>new draft version</strong> of the opinion</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-600 mt-0.5" />
                <span>The <strong>original version</strong> will remain unchanged</span>
              </div>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                <span><strong>Final responsibility</strong> remains with you as the signing advocate</span>
              </div>
            </div>
            
            <div className="border rounded-lg p-3 bg-blue-50">
              <p className="text-xs font-semibold text-blue-700 mb-1">CHANGE PREVIEW:</p>
              <p className="text-sm text-gray-600 line-through">{edit.original_text?.substring(0, 100)}...</p>
              <p className="text-sm text-green-700 font-medium mt-1">→ {edit.suggested_text?.substring(0, 100)}...</p>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowConfirmModal(false)} disabled={applying}>
              Cancel
            </Button>
            <Button 
              onClick={handleApplyChange} 
              disabled={applying}
              className="bg-green-600 hover:bg-green-700"
            >
              {applying ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Apply & Create New Version
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main AI Review Panel Component
const AIReviewPanel = ({ opinionId, isOpen, onClose, isOpinionSigned = false, onApplySuggestionComplete }) => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState(null);
  const [showReReviewPrompt, setShowReReviewPrompt] = useState(false);
  
  useEffect(() => {
    if (isOpen && opinionId) {
      fetchLatestReview();
    }
  }, [isOpen, opinionId]);
  
  const fetchLatestReview = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/opinion-review/latest/${opinionId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setReview(response.data.review);
    } catch (err) {
      console.error('Error fetching review:', err);
      setError('Failed to load review');
    } finally {
      setLoading(false);
    }
  };
  
  const triggerReview = async () => {
    setReviewing(true);
    setError(null);
    setShowReReviewPrompt(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/opinion-review/review/${opinionId}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setReview(response.data.review);
      toast.success('AI review completed');
    } catch (err) {
      console.error('Error triggering review:', err);
      setError(err.response?.data?.detail || 'Failed to run AI review');
      toast.error('Failed to run AI review');
    } finally {
      setReviewing(false);
    }
  };
  
  const recordAction = async (reviewId, actionType, actionData) => {
    const token = localStorage.getItem('token');
    await axios.post(
      `${API}/api/opinion-review/action`,
      { review_id: reviewId, action_type: actionType, action_data: actionData },
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
  };
  
  const applySuggestion = async (opinionId, reviewId, editIndex, edit) => {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API}/api/opinion-review/apply-suggestion`,
      { 
        opinion_id: opinionId, 
        review_id: reviewId, 
        edit_index: editIndex,
        original_text: edit.original_text,
        suggested_text: edit.suggested_text,
        location: edit.location,
        reason: edit.reason
      },
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    
    // Show re-review prompt after applying
    setShowReReviewPrompt(true);
    
    // Notify parent to refresh opinion data (for version update)
    if (onApplySuggestionComplete) {
      onApplySuggestionComplete(response.data);
    }
    
    return response.data;
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-y-0 right-0 w-[480px] bg-white shadow-2xl z-50 flex flex-col border-l">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-600" />
          <h2 className="font-semibold text-gray-800">AI Legal Review</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Content */}
      <ScrollArea className="flex-1 p-4">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}
        
        {error && !loading && (
          <div className="text-center py-8">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-gray-600">{error}</p>
            <Button 
              variant="outline" 
              onClick={triggerReview} 
              className="mt-4"
              disabled={reviewing}
            >
              {reviewing ? 'Analyzing...' : 'Try Again'}
            </Button>
          </div>
        )}
        
        {!loading && !review && !error && (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-700 mb-2">No review yet</h3>
            <p className="text-sm text-gray-500 mb-4">
              Run AI review to get insights on this legal opinion
            </p>
            <Button 
              onClick={triggerReview} 
              disabled={reviewing}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {reviewing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Bot className="w-4 h-4 mr-2" />
                  Run AI Review
                </>
              )}
            </Button>
          </div>
        )}
        
        {!loading && review && (
          <div className="space-y-4">
            {/* Re-review Prompt */}
            {showReReviewPrompt && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <RefreshCw className="w-4 h-4" />
                  <span className="font-medium text-sm">Opinion content changed</span>
                </div>
                <p className="text-xs text-blue-600 mb-2">Would you like to re-run AI review?</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={triggerReview} disabled={reviewing} className="bg-blue-600 hover:bg-blue-700">
                    {reviewing ? 'Analyzing...' : 'Re-run Review'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowReReviewPrompt(false)}>
                    Later
                  </Button>
                </div>
              </div>
            )}
            
            {/* Status & Summary */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <StatusBadge status={review.overall_status} />
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                      AP/TS Rules
                    </Badge>
                    <span className="text-xs text-gray-500">
                      v{review.review_version}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-700">{review.summary_for_lawyer}</p>
              </CardContent>
            </Card>
            
            {/* Bank Readiness Score */}
            {review.bank_readiness && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-blue-800">🏦 Bank Readiness</span>
                    <span className={`text-2xl font-bold ${
                      (review.bank_readiness.score || 0) >= 7 ? 'text-green-600' :
                      (review.bank_readiness.score || 0) >= 4 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {review.bank_readiness.score || '?'}/10
                    </span>
                  </div>
                  {review.bank_readiness.blocking_issues?.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-red-700">Blocking Issues:</span>
                      <ul className="text-xs text-red-600 mt-1 space-y-1">
                        {review.bank_readiness.blocking_issues.map((issue, i) => (
                          <li key={i}>• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Issues Count */}
            <div className="flex gap-2">
              <div className="flex-1 bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{review.issues_count?.high || 0}</div>
                <div className="text-xs text-red-600">High Risk</div>
              </div>
              <div className="flex-1 bg-yellow-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">{review.issues_count?.medium || 0}</div>
                <div className="text-xs text-yellow-600">Medium</div>
              </div>
              <div className="flex-1 bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{review.issues_count?.low || 0}</div>
                <div className="text-xs text-blue-600">Low</div>
              </div>
            </div>
            
            {/* Document Reconciliation - FIRST CHECK */}
            {review.document_reconciliation && (
              <Card className={`border-2 ${review.document_reconciliation?.status === 'HIGH_RISK' ? 'border-red-300 bg-red-50' : review.document_reconciliation?.status === 'NEEDS_REVIEW' ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`}>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    🔎 Document Reconciliation
                    <Badge className={`ml-auto ${review.document_reconciliation?.status === 'HIGH_RISK' ? 'bg-red-600' : review.document_reconciliation?.status === 'NEEDS_REVIEW' ? 'bg-amber-500' : 'bg-green-600'}`}>
                      {review.document_reconciliation?.status || 'PENDING'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Uploaded Documents:</span>
                      <span className="font-medium">{review.document_reconciliation?.uploaded_count || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Referenced in Opinion:</span>
                      <span className="font-medium">{review.document_reconciliation?.referenced_count || '—'}</span>
                    </div>
                    {review.document_reconciliation?.missing_from_opinion?.length > 0 && (
                      <div className="mt-2 p-2 bg-red-100 rounded border border-red-200">
                        <span className="text-xs font-semibold text-red-700">🔴 Uploaded but NOT in Opinion:</span>
                        <ul className="text-xs text-red-600 mt-1 list-disc list-inside">
                          {review.document_reconciliation.missing_from_opinion.map((doc, i) => (
                            <li key={i}>{doc}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {review.document_reconciliation?.extra_in_opinion?.length > 0 && (
                      <div className="mt-2 p-2 bg-amber-100 rounded border border-amber-200">
                        <span className="text-xs font-semibold text-amber-700">🟡 In Opinion but NOT Uploaded:</span>
                        <ul className="text-xs text-amber-600 mt-1 list-disc list-inside">
                          {review.document_reconciliation.extra_in_opinion.map((doc, i) => (
                            <li key={i}>{doc}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {review.document_reconciliation?.all_docs_considered && (
                      <div className="mt-2 p-2 bg-green-100 rounded border border-green-200">
                        <span className="text-xs font-semibold text-green-700">✅ All uploaded documents considered in opinion</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* AP/TS Rule Check Summary */}
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  📋 AP/TS Rule Checks
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {/* Chain of Title */}
                  <RuleCheckRow 
                    label="Chain of Title" 
                    status={review.chain_of_title?.status}
                    detail={review.chain_of_title?.period_years ? `${review.chain_of_title.period_years} years` : null}
                  />
                  {/* EC Analysis */}
                  <RuleCheckRow 
                    label="EC Coverage" 
                    status={review.ec_analysis?.status}
                    detail={review.ec_analysis?.gap_exists ? 'Gap detected' : null}
                  />
                  {/* Survey & Extent */}
                  <RuleCheckRow 
                    label="Survey/Extent" 
                    status={review.survey_extent?.status}
                    detail={review.survey_extent?.mismatches?.length > 0 ? 'Mismatches found' : null}
                  />
                  {/* Inheritance */}
                  {review.inheritance_succession?.applicable && (
                    <RuleCheckRow 
                      label="Succession" 
                      status={review.inheritance_succession?.status}
                      detail={review.inheritance_succession?.oral_partition_risk ? 'Oral partition risk' : null}
                    />
                  )}
                  {/* GPA */}
                  {review.gpa_agreement?.gpa_used && (
                    <RuleCheckRow 
                      label="GPA/Agreement" 
                      status={review.gpa_agreement?.status}
                      detail={!review.gpa_agreement?.gpa_registered ? 'Not registered' : null}
                    />
                  )}
                  {/* Land Restrictions */}
                  <RuleCheckRow 
                    label="Land Restrictions" 
                    status={review.land_type_restrictions?.status}
                    detail={
                      review.land_type_restrictions?.assigned_land || 
                      review.land_type_restrictions?.ceiling_land ||
                      review.land_type_restrictions?.inam_wakf_endowment
                        ? 'Restricted land detected' : null
                    }
                  />
                  {/* Approvals */}
                  <RuleCheckRow 
                    label="Layout/Approvals" 
                    status={review.approvals?.status}
                    detail={review.approvals?.layout_approval_needed && !review.approvals?.dtcp_hmda_present ? 'Missing approval' : null}
                  />
                  {/* Language Safety */}
                  <RuleCheckRow 
                    label="Language Safety" 
                    status={review.language_safety?.status}
                    detail={review.language_safety?.absolute_guarantees_found ? 'Absolute language found' : null}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Issues - Requires Advocate Review */}
            {review.issues && review.issues.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  ⚠️ Issues Requiring Advocate Review ({review.issues.length})
                </h3>
                <p className="text-xs text-gray-500 mb-3 ml-6">
                  These are legal or factual concerns that require your professional judgment. 
                  Review each issue and acknowledge after verification.
                </p>
                <div className="space-y-2">
                  {review.issues.map((issue, index) => (
                    <IssueCard 
                      key={index} 
                      issue={issue} 
                      index={index}
                      reviewId={review.id}
                      onAcknowledge={recordAction}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Suggested Edits - System Applicable */}
            {review.suggested_edits && review.suggested_edits.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-1 flex items-center gap-2">
                  <FileEdit className="w-4 h-4 text-blue-500" />
                  ✏️ Optional Language Improvements ({review.suggested_edits.length})
                </h3>
                <p className="text-xs text-gray-500 mb-3 ml-6">
                  These are tone and clarity improvements that do not change legal conclusions. 
                  Preview and apply with your confirmation.
                </p>
                <div className="space-y-3">
                  {review.suggested_edits.map((edit, index) => (
                    <SuggestedEditCard 
                      key={index} 
                      edit={edit} 
                      index={index}
                      reviewId={review.id}
                      opinionId={opinionId}
                      onAction={recordAction}
                      onApplySuggestion={applySuggestion}
                      isOpinionSigned={isOpinionSigned}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Final Note */}
            <div className="bg-gray-50 border rounded-lg p-3">
              <p className="text-xs text-gray-600 italic">
                ⚠️ {review.final_note || "This review is assistive only. Final responsibility rests with the signing advocate."}
              </p>
            </div>
            
            {/* Re-run Button */}
            <div className="pt-2">
              <Button 
                variant="outline" 
                onClick={triggerReview} 
                disabled={reviewing}
                className="w-full"
              >
                {reviewing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Re-analyzing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Re-run Review
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default AIReviewPanel;
